import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Calendar, Copy, Check, Loader2, Video, Phone, MapPin, Save, ExternalLink, Clock, Users, AlertCircle, UserX,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const WEEKDAYS = [
    { key: "mon", label: "Monday" },
    { key: "tue", label: "Tuesday" },
    { key: "wed", label: "Wednesday" },
    { key: "thu", label: "Thursday" },
    { key: "fri", label: "Friday" },
    { key: "sat", label: "Saturday" },
    { key: "sun", label: "Sunday" },
];

// Deliberately permissive: catches obvious typos ("notanemail", "foo@", "foo@bar")
// without rejecting legitimate corporate TLDs.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmployerScheduler = () => {
    const [page, setPage] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [pastBookings, setPastBookings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        try {
            const [pageRes, bookingsRes] = await Promise.all([
                api.get("/employer/scheduler"),
                api.get("/employer/scheduler/bookings?include_past=true"),
            ]);
            setPage(pageRes.data);
            setBookings(bookingsRes.data.results || []);
            setPastBookings(bookingsRes.data.past || []);
        } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggleNoShow = async (booking) => {
        const next = !booking.no_show;
        try {
            await api.post(`/employer/scheduler/bookings/${booking.id}/mark-no-show`, { no_show: next });
            setPastBookings((rows) => rows.map((b) => (b.id === booking.id ? { ...b, no_show: next } : b)));
            toast.success(next ? "Marked as no-show." : "No-show cleared.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const patch = (partial) => setPage((p) => ({ ...p, ...partial }));
    const patchDay = (key, ranges) => setPage((p) => ({ ...p, week: { ...p.week, [key]: ranges } }));

    const invalidEmails = useMemo(
        () => (page?.team_emails || []).filter((e) => !EMAIL_RE.test(e)),
        [page?.team_emails],
    );
    const offDomainEmails = useMemo(() => {
        const dom = (page?.expected_domain || "").toLowerCase();
        if (!dom) return [];
        return (page?.team_emails || []).filter(
            (e) => EMAIL_RE.test(e) && e.toLowerCase().split("@")[1] !== dom,
        );
    }, [page?.team_emails, page?.expected_domain]);

    const addRange = (key) => patchDay(key, [...(page.week[key] || []), { start: "09:00", end: "17:00" }]);
    const rmRange = (key, i) => patchDay(key, page.week[key].filter((_, idx) => idx !== i));

    const save = async () => {
        if (invalidEmails.length) {
            toast.error(`Fix invalid team email${invalidEmails.length > 1 ? "s" : ""}: ${invalidEmails.join(", ")}`);
            return;
        }
        setSaving(true);
        try {
            await api.put("/employer/scheduler", {
                slot_minutes: page.slot_minutes,
                advance_days: page.advance_days,
                buffer_minutes: page.buffer_minutes ?? 10,
                week: page.week,
                location_type: page.location_type,
                location_detail: page.location_detail || "",
                team_emails: page.team_emails || [],
            });
            toast.success("Availability saved.");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    if (!page) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    const shareUrl = `${window.location.origin}${page.public_url_path}`;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Booking link copied!");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy — please copy manually."); }
    };

    return (
        <div data-testid="employer-scheduler-page" className="space-y-6">
            <div>
                <p className="overline">Employer · Interview Scheduler</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Pick-a-time bookings</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Set your weekly availability once — every Smart Reply you send drops the booking link right into the email, so applicants can grab a slot in one tap.
                </p>
            </div>

            {/* Share link card */}
            <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-5">
                <p className="overline">Your booking link</p>
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
                    <code data-testid="scheduler-link" className="flex-1 truncate text-foreground">{shareUrl}</code>
                    <button onClick={copyLink} data-testid="scheduler-copy" className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90">
                        {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                    </button>
                    <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted">
                        <ExternalLink size={12} /> Open
                    </a>
                </div>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Config */}
                <div className="lg:col-span-2 card-soft p-5 space-y-5">
                    <p className="overline">Availability</p>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Meeting length</Label>
                            <Select value={String(page.slot_minutes)} onValueChange={(v) => patch({ slot_minutes: parseInt(v, 10) })}>
                                <SelectTrigger data-testid="scheduler-slot-len" className="mt-1.5 bg-background">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[15, 30, 45, 60, 90].map((m) => <SelectItem key={m} value={String(m)}>{m} min</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Buffer between</Label>
                            <Select value={String(page.buffer_minutes ?? 10)} onValueChange={(v) => patch({ buffer_minutes: parseInt(v, 10) })}>
                                <SelectTrigger data-testid="scheduler-buffer" className="mt-1.5 bg-background">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[0, 5, 10, 15, 20, 30, 45, 60].map((m) => <SelectItem key={m} value={String(m)}>{m === 0 ? "No buffer" : `${m} min`}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Book up to</Label>
                            <Select value={String(page.advance_days)} onValueChange={(v) => patch({ advance_days: parseInt(v, 10) })}>
                                <SelectTrigger data-testid="scheduler-advance" className="mt-1.5 bg-background">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {[7, 14, 21, 30, 45, 60].map((d) => <SelectItem key={d} value={String(d)}>{d} days out</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div>
                        <Label>Meeting type</Label>
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {[
                                { v: "video", icon: Video, label: "Video call" },
                                { v: "phone", icon: Phone, label: "Phone" },
                                { v: "in_person", icon: MapPin, label: "In person" },
                            ].map(({ v, icon: Icon, label }) => (
                                <button
                                    key={v}
                                    onClick={() => patch({ location_type: v })}
                                    data-testid={`scheduler-loc-${v}`}
                                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${page.location_type === v ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" : "border-border bg-card text-foreground hover:bg-muted"}`}
                                >
                                    <Icon size={12} /> {label}
                                </button>
                            ))}
                        </div>
                        <Textarea
                            data-testid="scheduler-loc-detail"
                            value={page.location_detail || ""}
                            onChange={(e) => patch({ location_detail: e.target.value })}
                            rows={2}
                            className="mt-2"
                            placeholder={page.location_type === "video" ? "Your Zoom / Google Meet link (visible on the booking page)" : page.location_type === "in_person" ? "Office address" : "Direct phone number"}
                        />
                    </div>

                    <div>
                        <Label>Weekly availability</Label>
                        <div className="mt-2 space-y-2">
                            {WEEKDAYS.map(({ key, label }) => (
                                <div key={key} className="flex items-center gap-2 flex-wrap" data-testid={`scheduler-day-${key}`}>
                                    <div className="w-24 text-xs font-semibold text-foreground">{label}</div>
                                    {(page.week[key] || []).map((r, i) => (
                                        <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-border bg-muted/30 px-2 py-1 text-xs">
                                            <Input type="time" value={r.start} onChange={(e) => { const w = [...page.week[key]]; w[i] = { ...w[i], start: e.target.value }; patchDay(key, w); }} data-testid={`scheduler-${key}-start-${i}`} className="h-7 w-24 text-xs" />
                                            <span className="text-muted-foreground">–</span>
                                            <Input type="time" value={r.end} onChange={(e) => { const w = [...page.week[key]]; w[i] = { ...w[i], end: e.target.value }; patchDay(key, w); }} data-testid={`scheduler-${key}-end-${i}`} className="h-7 w-24 text-xs" />
                                            <button onClick={() => rmRange(key, i)} className="text-muted-foreground hover:text-rose-600 text-lg leading-none">×</button>
                                        </span>
                                    ))}
                                    <button onClick={() => addRange(key)} data-testid={`scheduler-add-${key}`} className="text-xs font-semibold text-emerald-700 hover:underline">+ Add window</button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label>Team members (round-robin)</Label>
                        <p className="text-xs text-muted-foreground mt-1">Add teammate emails — Simonara rotates each new booking across the list so no single person carries all the interviews. Leave empty to keep every booking on you.</p>
                        <Textarea
                            data-testid="scheduler-team"
                            value={(page.team_emails || []).join("\n")}
                            onChange={(e) => patch({ team_emails: e.target.value.split(/[,\n]/).map((s) => s.trim()).filter(Boolean) })}
                            rows={3}
                            className="mt-2"
                            placeholder="alice@yourco.com&#10;bob@yourco.com&#10;carol@yourco.com"
                        />
                        {(page.team_emails || []).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="scheduler-team-chips">
                                {(page.team_emails || []).map((e, i) => {
                                    const bad = !EMAIL_RE.test(e);
                                    const offDomain = !bad && page.expected_domain &&
                                        e.toLowerCase().split("@")[1] !== page.expected_domain.toLowerCase();
                                    let cls = "";
                                    let tid = `scheduler-team-chip-${i}`;
                                    if (bad) { cls = "border-rose-500/60 text-rose-700 dark:text-rose-400 bg-rose-500/10"; tid = `scheduler-team-chip-invalid-${i}`; }
                                    else if (offDomain) { cls = "border-amber-500/60 text-amber-700 dark:text-amber-400 bg-amber-500/10"; tid = `scheduler-team-chip-offdomain-${i}`; }
                                    return (
                                        <Badge key={i} variant="outline" data-testid={tid} className={`text-[10px] ${cls}`}>
                                            {(bad || offDomain) && <AlertCircle size={9} className="mr-0.5 inline" />}
                                            {i === (page.round_robin_index ?? 0) && !bad ? "→ " : ""}{e}
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                        {invalidEmails.length > 0 && (
                            <p data-testid="scheduler-team-warning" className="mt-2 text-xs font-medium text-rose-600 dark:text-rose-400 flex items-center gap-1">
                                <AlertCircle size={11} /> {invalidEmails.length} invalid email{invalidEmails.length > 1 ? "s" : ""} — fix before saving.
                            </p>
                        )}
                        {invalidEmails.length === 0 && offDomainEmails.length > 0 && (
                            <p data-testid="scheduler-team-offdomain-warning" className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-400 flex items-center gap-1">
                                <AlertCircle size={11} /> {offDomainEmails.length} email{offDomainEmails.length > 1 ? "s" : ""} not on <code className="font-mono">@{page.expected_domain}</code> — double-check for typos. You can still save.
                            </p>
                        )}
                    </div>

                    <button onClick={save} disabled={saving || invalidEmails.length > 0} data-testid="scheduler-save" className="btn-green disabled:opacity-60">
                        {saving ? <><Loader2 size={13} className="mr-1.5 inline animate-spin" /> Saving…</> : <><Save size={13} className="mr-1.5 inline" /> Save availability</>}
                    </button>
                </div>

                {/* Upcoming bookings */}
                <div className="card-soft p-5">
                    <p className="overline">Upcoming interviews</p>
                    {bookings.length === 0 ? (
                        <div className="mt-4 text-sm text-muted-foreground">
                            No bookings yet. Once you send a Smart Reply, applicants will show up here.
                        </div>
                    ) : (
                        <div className="mt-3 space-y-2">
                            {bookings.map((b) => (
                                <div key={b.id} data-testid={`booking-row-${b.id}`} className="rounded-lg border border-border p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-foreground truncate">{b.candidate_name}</div>
                                            <div className="text-xs text-muted-foreground truncate">{b.candidate_email}</div>
                                        </div>
                                        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 text-[10px]"><Clock size={9} className="inline mr-0.5" /> Confirmed</Badge>
                                    </div>
                                    <div className="mt-2 text-xs text-muted-foreground">
                                        {new Date(b.start_iso).toLocaleString()}
                                    </div>
                                    {b.notes && <div className="mt-1 text-[11px] text-muted-foreground italic">&ldquo;{b.notes}&rdquo;</div>}
                                    <a
                                        href={`/interview/${b.id}?role=employer`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`booking-join-video-${b.id}`}
                                        className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                                    >
                                        <Video size={11} /> Join video interview
                                    </a>
                                </div>
                            ))}
                        </div>
                    )}

                    {pastBookings.length > 0 && (
                        <div className="mt-6 pt-5 border-t border-border" data-testid="scheduler-past-list">
                            <p className="overline">Past interviews (60d)</p>
                            <div className="mt-3 space-y-2">
                                {pastBookings.map((b) => (
                                    <div key={b.id} data-testid={`past-booking-row-${b.id}`} className={`rounded-lg border p-3 ${b.no_show ? "border-rose-500/50 bg-rose-500/5" : "border-border"}`}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-sm font-semibold text-foreground truncate">{b.candidate_name}</div>
                                                <div className="text-xs text-muted-foreground truncate">{b.candidate_email}</div>
                                            </div>
                                            {b.no_show
                                                ? <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-0 text-[10px]"><UserX size={9} className="inline mr-0.5" /> No-show</Badge>
                                                : <Badge className="bg-muted text-muted-foreground border-0 text-[10px]">Completed</Badge>
                                            }
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                            {new Date(b.start_iso).toLocaleString()}
                                        </div>
                                        <button
                                            onClick={() => toggleNoShow(b)}
                                            data-testid={`past-booking-noshow-${b.id}`}
                                            className={`mt-2 text-[11px] font-semibold hover:underline ${b.no_show ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}
                                        >
                                            {b.no_show ? "↺ Clear no-show" : "Mark as no-show →"}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployerScheduler;
